import React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Stack,
  RadioGroup,
  Radio,
  Center,
  Text,
  GridItem,
  Select
} from '@chakra-ui/react';
import ProcyonChart from './ProcyonChart';
import data from './results.json';
import llama3 from './AITextGenerationLlama3.json';
import sdLight from './AIImageGenerationSDLight.json';
import computerVision20 from './AIComputerVision20.json';
import computerVisionScore from './AIComputerVisionScore.json';

const NPU_DATA_MAP = {
  AITextGenerationLlama3: llama3,
  AIImageGenerationSDLight: sdLight,
  AIComputerVision20: computerVision20,
  AIComputerVisionScore: computerVisionScore,
};

const HomePage = () => {
    const [optSelected, setOptSelected] = useState('office_gen');
    const [yearSelected, setYearSelected] = useState('0');
    const [dataArr, setDataArr] = useState(data.results.office_gen[0]);
    const [labelArr, setLabelArr] = useState(data.labels.office_gen[0]);
    const [tooltipData, setTooltipData] = useState(data.machines.office_gen[0]);
    const [titleData, setTitleData] = useState('Office Productivity Benchmark GEN scores');
    const [levelsData, setLevelData] = useState(data.levels.office_gen[0]);
    
    // NEW STATES: Lifted up from ProcyonChart to resolve errors
    const [expandedIndex, setExpandedIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('example1');

    // Effect to reset the active tab when switching selected boxes
    useEffect(() => {
      const selectedData = expandedIndex !== null ? tooltipData[expandedIndex] : null; 
      if (selectedData) {
        const tabs = ['example1', 'example2', 'example3'];
        const firstValidTab = tabs.find(tab => {
          const t = selectedData[tab];
          return t && (t.cpu?.trim() || t.igpu?.trim() || t.dgpu?.trim());
        });
        setActiveTab(firstValidTab || 'example1');
      }
    }, [expandedIndex, tooltipData]);
    
    const onOptionSelected = (opt) => {
      if (typeof opt === 'string' || opt instanceof String) {
      } else {
        opt = opt.target.value
      }
      const tempYearSelected = yearSelected;
      setYearSelected(tempYearSelected);
      switch (opt) {
        case 'office_gen':
          setOptSelected('office_gen');
          setDataArr(data.results.office_gen[yearSelected]);
          setLabelArr(data.labels.office_gen[yearSelected]);
          setTooltipData(data.machines.office_gen[yearSelected]);
          setTitleData('Office Productivity Benchmark GEN scores');
          setLevelData(data.levels.office_gen[yearSelected]);
          break;
        case 'office_unplugged':
          setOptSelected('office_unplugged');
          setDataArr(data.results.office_unplugged[yearSelected]);
          setLabelArr(data.labels.office_unplugged[yearSelected]);
          setTooltipData(data.machines.office_unplugged[yearSelected]);
          setTitleData('Office Productivity Benchmark UPLUGGED scores');
          setLevelData(data.levels.office_unplugged[yearSelected]);
          break;
        case 'photo':
          setOptSelected('photo');
          setDataArr(data.results.photo[yearSelected]);
          setLabelArr(data.labels.photo[yearSelected]);
          setTooltipData(data.machines.photo[yearSelected]);
          setTitleData('Photo Editing Benchmark scores');
          setLevelData(data.levels.photo[yearSelected]);
          break;
        case 'video':
          setOptSelected('video');
          setDataArr(data.results.video[yearSelected]);
          setLabelArr(data.labels.video[yearSelected]);
          setTooltipData(data.machines.video[yearSelected]);
          setTitleData('Video Editing Benchmark scores');
          setLevelData(data.levels.video[yearSelected]);
          break;
        case 'AITextGenerationLlama3':
          setOptSelected('AITextGenerationLlama3');
          setTitleData('AI Text Generation Llama3 scores');
          removeTooltip();
          break;
        case 'AIImageGenerationSDLight':
          setOptSelected('AIImageGenerationSDLight');
          setTitleData('AI Image Generation SD Light scores');
          removeTooltip();
          break;
        case 'AIComputerVision20':
          setOptSelected('AIComputerVision20');
          setTitleData('AI Computer Vision 2.0 scores');    
          removeTooltip(); 
          break;
        case 'AIComputerVisionScore':
          setOptSelected('AIComputerVisionScore');
          setTitleData('AI Computer Vision Score scores');    
          removeTooltip(); 
          break;
        default:
          break;
      }
    }

    const removeTooltip = () => {
      var elementTooltip =  document.getElementById('chartjs-tooltip');
      var elementTooltipLine = document.getElementById('chartjs-tooltip-line');
      if (typeof(elementTooltip) != 'undefined' && elementTooltip != null) {
        document.getElementById("chartjs-tooltip").remove();
      }

      if (typeof(elementTooltipLine) != 'undefined' && elementTooltipLine != null) {
        document.getElementById("chartjs-tooltip-line").remove();
      }
    }

    const buildTableNpu = (currentOpt) => {
      // Dynamically selects the corresponding JSON file using the dictionary object
      const targetNpuData = NPU_DATA_MAP[currentOpt];

      // Safety fallback check to prevent component crashes if data structure is missing
      if (!targetNpuData || !targetNpuData.legend || !targetNpuData.content) {
        return null;
      }

      return (
        <div className='tableNpu'>
          <div className='legendNpu'>
            {
              targetNpuData.legend.map((value, index) => {
                return (<div key={`legend-${index}`}>{value}</div>);
              })
            }
          </div>
          <div className='contentNpu'>
            {
              targetNpuData.content.map((value, rowIndex) => {
                return ( 
                  <div className='contentRow' key={`row-${rowIndex}`}>
                    {
                      value.map((info, cellIndex) => {
                        return (<div className='childrenContent' key={`cell-${rowIndex}-${cellIndex}`}>{info}</div>);
                      })
                    }
                  </div> 
                );
              })
            }
          </div>
        </div>
      );
    };

    return (
        <Box textAlign="center" fontSize="xl">
            <Grid
                /*templateAreas={`"header header"
          "nav main"
          "footer footer"`}*/
                templateAreas={{base: '"header header" "nav nav" "main main" "footer footer"', lg: '"header header" "nav main""footer footer"', md: '"header header" "nav nav" "main main" "footer footer"', sm:'"header header" "nav nav" "main main" "footer footer"' }}
                gridTemplateRows={{base: '50px 1fr 1fr 0px', lg: '50px 1fr 30px', md: '50px 1fr auto 0px', sm: '50px 1fr auto 0px' }}
                gridTemplateColumns={'320px 1fr'}
                h='200px'
                gap='0'
                color='blackAlpha.700'
            >
                <GridItem pl={{base: '20px', lg: '40px', md: '30px', sm: '20px'}} area={'header'} display='inline-grid' alignItems='center' textAlign='left' borderBottom='1px' borderBottomColor='#E7E7E7' fontSize='15px' color='#59595A' fontWeight='600'>
                  Score Range & Distribution of PC's Tested by UL Solutions
                </GridItem>
                <GridItem pl={{base: '0px', lg: '20px', md: '10px', sm: '0px'}} area={'nav'} borderRight={{base: '0px', lg: '1px', md: '0px', sm: '0px'}} borderRightColor={{base: '#E7E7E7', lg: '#E7E7E7', md: '#E7E7E7', sm: '#E7E7E7'}} fontSize='15px' color='#59595A' height={{base: 'auto', xl:'850px', lg: 'auto', md: 'auto', sm: 'auto'}}>
                    <Box display={{base: 'block', lg: 'none', md: 'block', sm: 'block'}} pl={{base: '20px', lg: '0', md: '20px', sm: '20px'}} pr={{base: '20px', lg: '0', md: '20px', sm: '20px'}} mt={{base: '20px', lg: '0', md: '20px', sm: '20px'}}>
                      <Text fontSize='15px' display={{base: 'inline', lg: 'none', md: 'inline', sm: 'inline'}}>Computers performance scores with</Text> <Text display={{base: 'inline', lg: 'none', md: 'inline', sm: 'inline'}} fontSize='15px' color='#C02033' fontWeight='bold'>{titleData}</Text> <Text display={{base: 'inline', lg: 'none', md: 'inline', sm: 'inline'}} fontSize='15px'>in 2026</Text>
                    </Box> 
                    <Center>
                        <Box pt={{base: '20px', lg: '40px', md: '30px', sm: '30px'}} pl={{base: '0px', lg: '40px', md: '0px', sm: '0px'}} pr={{base: '0px', lg: '40px', md: '0px', sm: '0px'}} pb='40px' width={{base: '90%', lg: 'auto', md: '70%', sm: '85%'}}>
                            <Text marginBottom={{base: '15px', lg: '20px', md: '10px', sm: '10px'}} textAlign='left'>Choose a benchmark type</Text>
                            <RadioGroup value={optSelected} onChange={onOptionSelected} width='210px' display={{base: 'none', lg: 'block', md: 'none', sm: 'none'}} textAlign='left'>
                                <Stack>
                                    <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="office_gen"  colorScheme='red'>Office productivity</Radio>
                                    <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="photo"  colorScheme='red'>Photo Editing</Radio>
                                    <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="video"  colorScheme='red'>Video Editing</Radio>
                                    {yearSelected ==='0' ? <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="AITextGenerationLlama3" colorScheme='red'>AI Text Generation Llama3 NPU</Radio> : ''}
                                    {yearSelected ==='0' ? <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="AIImageGenerationSDLight" colorScheme='red'>AI Image Generation SD Light NPU</Radio> : ''}
                                    {yearSelected ==='0' ? <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="AIComputerVision20" colorScheme='red'>AI Computer Vision 2.0 NPU</Radio> : ''}
                                    {yearSelected ==='0' ? <Radio pb={{base: '15px', lg: '10px', md: '10px', sm: '10px'}} size="sm" value="AIComputerVisionScore" colorScheme='red'>AI Computer Vision Score NPU</Radio> : ''}
                                </Stack>
                            </RadioGroup>
                            <Select className='chooseBenchmark' value={optSelected} onChange={onOptionSelected} display={{base: 'block', lg: 'none', md: 'block', sm: 'block'}}>
                              <option value="office_gen">Office productivity</option>
                              <option value="photo">Photo Editing</option>
                              <option value="video">Video Editing</option>
                              {yearSelected === '0' ? <option value="AITextGenerationLlama3">AI Text Generation Llama3 NPU</option> : ''}
                              {yearSelected === '0' ? <option value="AIImageGenerationSDLight">AI Image Generation SD Light NPU</option> : ''}
                              {yearSelected === '0' ? <option value="AIComputerVision20">AI Computer Vision 2.0 NPU</option> : ''}
                              {yearSelected === '0' ? <option value="AIComputerVisionScore">AI Computer Vision Score NPU</option> : ''}
                            </Select>
                        </Box>
                    </Center>
                </GridItem>
                <GridItem pl={{base: '15px', lg: '30px', md: '15px', sm: '15px'}} pr={{base: '15px', lg: 'auto', md: 'auto', sm: '15px'}} pt={{base: '5px', lg: '40px', md: '20px', sm: '20px'}} area={'main'} fontSize='15px' textAlign='left' width={{base: '100%', lg: 'auto', md: '70%', sm: '100%'}} ml={{base: 'auto', lg: '30px', md: 'auto', sm: 'auto'}} mr={{base: 'auto', lg: 'auto', md: 'auto', sm: 'auto'}}>
                  <Box display={{base: 'block', lg: 'block', md: 'block', sm: 'block'}}>
                    <Text fontSize='15px' display={{base: 'none', lg: 'inline', md: 'none', sm: 'none'}}> Computers performance scores with</Text> <Text display={{base: 'none', lg: 'inline', md: 'none', sm: 'none'}} fontSize='15px' color='#C02033' fontWeight='bold'>{titleData}</Text> <Text display={{base: 'none', lg: 'inline', md: 'none', sm: 'none'}} fontSize='15px'>in 2026</Text>
                  </Box>
                  <Box id='graphContainer' display={{base: 'flex', lg: 'flex', md: 'block', sm: 'block'}} flexWrap={{base: 'wrap', lg: 'wrap', md: 'wrap', sm: 'wrap'}} gap={15} position={{base: 'relative', lg: 'relative', md: 'relative', sm: 'relative'}} width={{base: 'auto', lg: '600px', md: '600px', sm: 'auto'}} height={{base: 'auto', lg: 'auto', md: 'auto', sm: 'auto'}} mt={{base: '10px', lg: '10px', md: '10px', sm: '10px'}} mb={{base: '0px', lg: '20px', md: 'auto', sm: '0px'}}>
                    {(optSelected === 'office_gen' || optSelected === 'photo' || optSelected === 'video') ? <ProcyonChart chartData={dataArr} labelsChartData={labelArr} tooltipChartData={tooltipData} titleBenchmark={titleData} levelsBenchmark={levelsData} expandedIndex={expandedIndex} setExpandedIndex={setExpandedIndex} activeTab={activeTab} setActiveTab={setActiveTab}>
                    </ProcyonChart> : buildTableNpu(optSelected)}
                  </Box>
                </GridItem>
                <GridItem pl='2' mt={{base: '0px', lg: '20px', md: '20px', sm: '0px'}} area={'footer'} textAlign='left' fontSize='12px' pl={{base: '40px', lg: '4px', md: '40px', sm: '40px'}} pr={{base: '40px', lg: '4px', md: '40px', sm: '40px'}} maxW='1030px'>
                   <Box display={{base: 'block', lg: 'block', md: 'block', sm: 'block'}}>
                    <Text fontSize='15px' display={{base: 'block', lg: 'block', md: 'block', sm: 'block'}}> Procyon benchmarks measure performance in real-world tasks using industry standard software, helping achieve up-to-date understanding of a system’s performance.</Text>
                  </Box>
                  <Box display={{base: 'block', lg: 'block', md: 'block', sm: 'block'}} mt={{base: '0px', lg: '20px', md: '20px', sm: '0px'}}>
                    <Text fontStyle='italic' fontSize='15px' display={{base: 'block', lg: 'block', md: 'block', sm: 'block'}}> These tier levels and example systems are based on a sample of approximately 250 devices each year and, should not be considered a representative sample of PCs and laptops sold by Elkjøp, or deemed as a representative sample of PCs and laptops sold globally</Text>
                  </Box>
                </GridItem>
            </Grid>
        </Box>
    );
}

export default HomePage;
